import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { X, RotateCcw, Archive } from 'lucide-react'
import { Button } from '../ui/button'
import { Avatar } from '../ui/avatar'
import { formatMessageTime } from '../../utilities/messageHelpers'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'

function ArchivedConversationsModal({ isOpen, onClose, fetchArchivedConversations, onRestore }) {
  const [archivedConversations, setArchivedConversations] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isRestoring, setIsRestoring] = useState(null)

  useEffect(() => {
    if (isOpen) {
      loadArchivedConversations()
    }
  }, [isOpen])

  const loadArchivedConversations = async () => {
    setIsLoading(true)
    try {
      const conversations = await fetchArchivedConversations()
      setArchivedConversations(conversations)
    } catch (error) {
      console.error('Error fetching archived conversations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRestore = async (conversationId) => {
    setIsRestoring(conversationId)
    try {
      await onRestore(conversationId)
      setArchivedConversations((prev) => prev.filter((c) => c.id !== conversationId))
    } catch (error) {
      console.error('Error restoring conversation:', error)
    } finally {
      setIsRestoring(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Archived Conversations</h2>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner size="8" color="purple" />
            </div>
          ) : archivedConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Archive className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No archived conversations</h3>
              <p className="text-sm text-gray-500">Conversations you archive will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {archivedConversations.map((conversation) => {
                const attrs = conversation.attributes || {}
                const otherParticipant = attrs.otherParticipant || {}
                const isCurrentlyRestoring = isRestoring === conversation.id

                return (
                  <div key={conversation.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-12 h-12 flex-shrink-0">
                        {otherParticipant.cloudinaryPhotoPublicId ? (
                          <img
                            src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_48,w_48/${otherParticipant.cloudinaryPhotoPublicId}.jpg`}
                            alt={otherParticipant.preferredName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold">
                            {(otherParticipant.preferredName || otherParticipant.fullName || '?')[0].toUpperCase()}
                          </div>
                        )}
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <p className="font-medium text-gray-900 truncate">
                            {otherParticipant.preferredName || otherParticipant.fullName}
                          </p>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatMessageTime(attrs.lastMessageAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate mb-2">
                          {attrs.lastMessagePreview || 'No messages yet'}
                        </p>
                        <Button
                          onClick={() => handleRestore(conversation.id)}
                          disabled={isCurrentlyRestoring}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                          {isCurrentlyRestoring ? 'Restoring...' : 'Restore'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ArchivedConversationsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fetchArchivedConversations: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired
}

export default ArchivedConversationsModal
